<template>
  <div
    id="communicationcard"
    class="communicationcard"
    :style="
      maxWidth
        ? `width: 100%; max-width: ${maxWidth}`
        : 'width: 100%; max-width: 100%'
    "
  >
    <v-card class="mx-auto my-4" max-width="100%" :outlined="outlined" flat>
      <v-toolbar
        v-if="showListToolbar"
        :color="toolbarColor"
        class="rounded-t"
        style="cursor: pointer; user-select: none"
        role="button"
        tabindex="0"
        :aria-expanded="listExpanded ? 'true' : 'false'"
        dark
        dense
        flat
        @click="toggleList"
        @keydown.enter.prevent="toggleList"
        @keydown.space.prevent="toggleList"
      >
        <v-toolbar-title
          class="text-subtitle-1 font-weight-medium"
          style="flex: 0 1 auto; max-width: 60%"
        >
          {{ subject }}
        </v-toolbar-title>

        <div
          class="d-flex align-center justify-end mr-1"
          style="flex: 1 1 0; min-width: 0; overflow: hidden"
        >
          <v-slide-y-transition mode="out-in">
            <span
              v-if="showTitlePreview"
              :key="previewIndex"
              class="text-body-1 text-truncate"
              aria-hidden="true"
            >
              {{ previewTitle }}
            </span>
          </v-slide-y-transition>
        </div>

        <v-icon aria-hidden="true">
          {{ listExpanded ? "mdi-chevron-up" : "mdi-chevron-down" }}
        </v-icon>
      </v-toolbar>

      <v-row dense>
        <v-col cols="12" v-if="showCardTitle || show?.includes('BODY')">
          <v-card-title class="text-h5 pb-0" v-if="showCardTitle">{{
            subject
          }}</v-card-title>
          <v-card-text class="py-0" v-if="show?.includes('BODY')">
            <div class="pt-2 tiptap" v-html="body"></div>
          </v-card-text>
        </v-col>

        <v-col cols="12" v-if="showList" class="py-0">
          <v-expand-transition>
            <div v-show="listVisible">
              <v-list dense class="py-0 mx-n1 transparent">
                <template v-for="(listItem, listItemIndex) in parsedListItems">
                  <v-list-group
                    v-if="listItem.description"
                    :key="'list-group-' + listItemIndex"
                    no-action
                  >
                    <template v-slot:activator>
                      <v-list-item-icon class="my-2 mr-4">
                        <v-icon>{{ listItem.icon }}</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content class="py-1">
                        <v-list-item-title class="text-body-2">
                          {{ listItem.title }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </template>

                    <v-list-item class="pl-14 pr-4" style="min-height: 0">
                      <v-list-item-content class="pt-0 pb-2">
                        <div
                          class="text-body-2 text--secondary communication-list-description"
                        >
                          {{ listItem.description }}
                        </div>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list-group>

                  <v-list-item v-else :key="'list-item-' + listItemIndex">
                    <v-list-item-icon class="my-2 mr-4">
                      <v-icon>{{ listItem.icon }}</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content class="py-1">
                      <v-list-item-title class="text-body-2">
                        {{ listItem.title }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-list>
            </div>
          </v-expand-transition>
        </v-col>

        <v-col cols="12" v-if="show?.includes('IMAGES')">
          <v-carousel
            v-model="carouselIndex"
            cycle
            hide-delimiters
            show-arrows
            :interval="4000"
            height="min-content"
          >
            <v-carousel-item v-for="(imageKey, index) in images" :key="index">
              <v-sheet
                height="100%"
                tile
                class="d-flex align-center justify-center pa-4"
              >
                <v-img
                  :src="imageUrls[index]"
                  height="250"
                  contain
                  :alt="'Image ' + (index + 1)"
                  :loading="!imageUrls[index]"
                >
                  <template v-slot:placeholder>
                    <v-row class="fill-height ma-0 align-center justify-center">
                      <v-progress-circular
                        indeterminate
                        color="primary"
                      ></v-progress-circular>
                    </v-row>
                  </template>
                </v-img>
              </v-sheet>
            </v-carousel-item>
          </v-carousel>
        </v-col>

        <v-col cols="12" v-if="show?.includes('VIDEO')">
          <v-card-text>
            <VideoEmbed :src="videoSrc" />
          </v-card-text>
        </v-col>

        <v-col cols="12" v-if="show?.includes('COUNTDOWN')">
          <CountdownTimer
            :targetDateTime="countDownTo"
            :class="outlined ? 'mb-3' : ''"
          />
        </v-col>
      </v-row>
    </v-card>
  </div>
</template>

<script>
import VideoEmbed from "@/components/global-components/VideoEmbed.vue";
import { Storage } from "aws-amplify"; // Import AWS Amplify's Storage module
import CountdownTimer from "@/components/global-components/CountdownTimer.vue";

export default {
  name: "CommunicationCard",
  components: {
    VideoEmbed,
    CountdownTimer,
  },
  props: {
    show: {
      type: Array,
      required: false,
      default: () => [],
    },
    images: {
      type: Array,
      required: false,
    },
    videoSrc: {
      type: String,
      required: false,
      default: "",
    },
    subject: {
      type: String,
      required: false,
      default: "",
    },
    body: {
      type: String,
      required: false,
      default: "",
    },
    countDownTo: {
      type: String,
      required: false,
      default: "",
    },
    listItems: {
      // Accepts a parsed array or the raw AWSJSON string straight off the model
      type: [Array, String],
      required: false,
      default: () => [],
    },
    toolbarColor: {
      type: String,
      required: false,
      default: "primary",
    },
    titleRotateInterval: {
      type: Number,
      required: false,
      default: 3000,
    },
    outlined: {
      type: Boolean,
      required: false,
      default: true,
    },
    maxWidth: {
      type: String,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      carouselIndex: 0, // To control the carousel's current slide
      imageUrls: [], // Array to store the resolved image URLs
      defaultListIcon: "mdi-circle-small", // Keeps rows aligned when an item has no icon
      listExpanded: false,
      previewIndex: 0,
      previewTimer: null,
    };
  },
  computed: {
    parsedListItems() {
      let listItems = this.listItems;

      if (typeof listItems === "string") {
        try {
          listItems = JSON.parse(listItems);
        } catch (error) {
          console.log(error);
          return [];
        }
      }

      if (!Array.isArray(listItems)) return [];

      return listItems
        .map((listItem) => ({
          icon: listItem?.icon || this.defaultListIcon,
          title: (listItem?.title || "").trim(),
          description: (listItem?.description || "").trim(),
        }))
        .filter(
          (listItem) => listItem.title !== "" || listItem.description !== "",
        );
    },

    showList() {
      return this.show?.includes("LIST") && this.parsedListItems.length > 0;
    },

    // The subject moves into the toolbar when a list is shown, so it reads as a
    // header for the list rather than sitting above it as a second title.
    showListToolbar() {
      return (
        this.showList &&
        this.show?.includes("SUBJECT") &&
        (this.subject || "").trim() !== ""
      );
    },

    showCardTitle() {
      return this.show?.includes("SUBJECT") && !this.showListToolbar;
    },

    // Only collapsible when there is a toolbar to click. Without one there
    // would be no way to reopen the list, so it stays permanently visible.
    listExpandable() {
      return this.showListToolbar;
    },

    listVisible() {
      return !this.listExpandable || this.listExpanded;
    },

    listItemTitles() {
      return this.parsedListItems
        .map((listItem) => listItem.title)
        .filter((title) => title !== "");
    },

    previewTitle() {
      if (this.listItemTitles.length === 0) return "";
      return this.listItemTitles[
        this.previewIndex % this.listItemTitles.length
      ];
    },

    showTitlePreview() {
      return (
        this.listExpandable &&
        !this.listExpanded &&
        this.listItemTitles.length > 0
      );
    },
  },
  watch: {
    images() {
      if (this.show?.includes("IMAGES")) {
        this.fetchImageUrls();
      }
    },
    showTitlePreview: {
      immediate: true,
      handler(visible) {
        if (visible) {
          this.startTitleRotation();
        } else {
          this.stopTitleRotation();
        }
      },
    },
    listItemTitles() {
      this.previewIndex = 0;
      if (this.showTitlePreview) this.startTitleRotation();
    },
  },
  methods: {
    toggleList() {
      if (!this.listExpandable) return;
      this.listExpanded = !this.listExpanded;
    },

    startTitleRotation() {
      this.stopTitleRotation();

      // Nothing to rotate through, or the user has asked for less motion.
      if (this.listItemTitles.length < 2) return;
      if (this.prefersReducedMotion()) return;

      this.previewTimer = setInterval(() => {
        this.previewIndex =
          (this.previewIndex + 1) % this.listItemTitles.length;
      }, this.titleRotateInterval);
    },

    stopTitleRotation() {
      if (!this.previewTimer) return;
      clearInterval(this.previewTimer);
      this.previewTimer = null;
    },

    prefersReducedMotion() {
      return (
        typeof window !== "undefined" &&
        typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches
      );
    },
    async fetchImageUrls() {
      const imageUrlsPromises = this.images.map(async (imageKey) => {
        try {
          const result = await Storage.get(imageKey, { level: "public" });
          return result; // Return the resolved URL
        } catch (error) {
          console.error("Error fetching image from S3:", error);
          return ""; // Return an empty string if there's an error
        }
      });

      // Wait for all the promises to resolve
      this.imageUrls = await Promise.all(imageUrlsPromises);
    },
  },
  mounted() {
    // Fetch image URLs when the component is mounted
    if (this.show?.includes("IMAGES")) {
      this.fetchImageUrls();
    }
  },
  beforeDestroy() {
    this.stopTitleRotation();
  },
};
</script>

<style>
#communicationcard h1 {
  font-size: 2em !important;
  margin-block-start: 0.67em !important;
  margin-block-end: 0.67em !important;
}

.communicationcard .v-list,
.communicationcard .v-list-item {
  max-width: 100%;
}

.communicationcard .v-list-item__content {
  min-width: 0;
}

.communicationcard .v-list-item__title,
.communicationcard .communication-list-description {
  overflow-wrap: anywhere;
}
</style>

<style scoped>
.vimeo-container {
  max-width: 100%;
  margin: 0 auto;
}

.vid-div {
  position: relative;
  width: 100%;
  overflow: hidden;
  padding-top: 56.25%; /* 16:9 Aspect Ratio */
  margin-bottom: 1.2rem;
}

.responsive-iframe {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  width: 100%;
  height: 100%;
  border: none;
}

/* Style for images */
.v-img {
  margin-bottom: 15px;
}

/* TipTap-specific table styling */
.tiptap {
  table {
    border-collapse: collapse;
    table-layout: fixed;
    max-width: 100% !important;
    width: auto !important;
    min-width: 200px !important;
    margin: 0;
    overflow: hidden;

    td,
    th {
      min-width: 1em;
      border: 2px solid #ced4da;
      padding: 3px 5px;
      vertical-align: top;
      box-sizing: border-box;
      position: relative;

      > * {
        margin-bottom: 0;
      }
    }

    th {
      font-weight: bold;
      text-align: left;
      background-color: #f1f3f5;
    }

    .selectedCell:after {
      z-index: 2;
      position: absolute;
      content: "";
      left: 0;
      right: 0;
      top: 0;
      bottom: 0;
      background: rgba(200, 200, 255, 0.4);
      pointer-events: none;
    }

    .column-resize-handle {
      position: absolute;
      right: -2px;
      top: 0;
      bottom: -2px;
      width: 4px;
      background-color: #adf;
      pointer-events: none;
    }
  }

  li {
    p {
      margin-bottom: 0;
    }
  }

  ul {
    margin-bottom: 16px;
  }

  ol {
    margin-bottom: 16px;
  }
}

.tableWrapper {
  overflow-x: auto;
}
</style>
